import React, { useRef, useState } from "react";
import styles from "./StackedBarChart.module.css";

export default function StackedBarChart({
  data,
  buckets,
  labelWidth = 90,
  showLegend = true,
}) {
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const containerRef = useRef(null);

  const rows = Object.entries(data).filter(([, values]) =>
    selectedBucket ? values[selectedBucket] > 0 : true
  );

  const visibleBuckets = selectedBucket
    ? buckets.filter(({ key }) => key === selectedBucket)
    : buckets;

  const updateTooltip = (event, rowName, bucket, value) => {
    const rect = containerRef.current?.getBoundingClientRect();

    if (!rect) return;

    setTooltip({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top - 10,
      text: `${rowName} — ${bucket.label}: ${value}`,
    });
  };

  const toggleBucket = (key) => {
    setSelectedBucket((current) =>
      current === key ? null : key
    );
  };

  return (
    <div ref={containerRef} className="position-relative">

      {/* Chart */}
      {rows.map(([rowName, values]) => (
        <div
          key={rowName}
          className="d-flex align-items-center gap-3 mb-2"
        >
          <div
            className="text-secondary fw-medium small text-truncate flex-shrink-0"
            style={{ width: labelWidth }}
          >
            {rowName}
          </div>

          <div className={styles.barTrack}>
            {visibleBuckets
              .filter(({ key }) => values[key] > 0)
              .map((bucket) => {
                const value = values[bucket.key];

                return (
                  <div
                    key={bucket.key}
                    className={`${styles.segment} ${styles[bucket.key]}`}
                    style={{ flexGrow: value }}
                    onMouseEnter={(event) =>
                      updateTooltip(event, rowName, bucket, value)
                    }
                    onMouseMove={(event) =>
                      updateTooltip(event, rowName, bucket, value)
                    }
                    onMouseLeave={() => setTooltip(null)}
                  >
                    <span className={styles.segLabel}>
                      {value}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      ))}

      {/* Tooltip */}
      {tooltip && (
        <div
          className={styles.tooltip}
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Legend */}
      {showLegend && (
        <div className="d-flex flex-wrap gap-1 mt-3">
          {buckets.map((bucket) => {
            const active = selectedBucket === bucket.key;

            return (
              <button
                key={bucket.key}
                type="button"
                className={`${styles.legendButton} ${
                  active ? styles.legendActive : ""
                }`}
                onClick={() => toggleBucket(bucket.key)}
              >
                <span
                  className={`${styles.dot} ${styles[bucket.key]}`}
                />

                <span>{bucket.label}</span>

                {bucket.warning && (
                  <span className={styles.warning}>⚠</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}